import { Button, Callout, SkeletonText } from '@dev-spendesk/grapes';

import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { formatMoney } from 'src/core/utils/money';

import {
  getCalloutTitleKey,
  getPeriodTranslationKey,
} from './calloutTranslationHelpers';
import { useSpendLimitStatusByRequestQuery } from '../../hooks/useSpendLimitStatusByRequestQuery';
import { type SpendLimitPeriodStatus } from '../../models/spendLimit';
import {
  calculateMaxReimbursableAmount,
  calculateUsedBeforeCurrentRequest,
} from '../../utils';

type ExceedLimitItemProps = {
  status: SpendLimitPeriodStatus;
  persistedRequestAmount: number;
};

export const StatusByRequestLimitCallout = ({
  requestId,
  requestAmount,
  originalRequestAmount,
  className,
  setReimbursedAmount,
}: {
  requestId: string;
  requestAmount: number;
  originalRequestAmount?: number;
  className?: string;
  setReimbursedAmount?: (reimbursedAmount: number) => void;
}) => {
  const { t } = useTranslation('global');
  const spendLimitStatusQueryState = useSpendLimitStatusByRequestQuery({
    requestId,
    enabled: true,
  });

  const renderExceedLimitItem = ({
    status,
    persistedRequestAmount,
  }: ExceedLimitItemProps) => {
    const periodKey = getPeriodTranslationKey(status.period);
    const usedBeforeCurrentRequest = calculateUsedBeforeCurrentRequest(
      status,
      persistedRequestAmount,
    );
    const totalImpactingCount =
      status.impactingSpends.requestCount +
      status.impactingSpends.paymentCount -
      1; // Exclude current request
    const hasUsedAmount = usedBeforeCurrentRequest > 0;

    return (
      <li key={`exceeded-${status.period}`}>
        {t(periodKey)} limit: {formatMoney(status.limitAmount, status.currency)}
        {t('spendLimits.modal.request.calloutWarning.alreadyUsed', {
          amount: formatMoney(usedBeforeCurrentRequest, status.currency),
        })}
        {hasUsedAmount && totalImpactingCount > 0 && (
          <>
            {' '}
            {t('spendLimits.modal.request.calloutWarning.acrossRequests', {
              count: totalImpactingCount,
            })}
          </>
        )}{' '}
        {t('spendLimits.modal.request.calloutWarning.excludingThisExpense')}
      </li>
    );
  };

  // TODO: handle 404 specifically
  if (spendLimitStatusQueryState.status === 'error') {
    return null;
  }

  return (
    <QuerySuspense
      loading={<CalloutLoading />}
      queryState={spendLimitStatusQueryState}
      fallback={(error) => (
        <QueryError queryError={error} componentType="Callout" />
      )}
    >
      {({ spendLimitStatus }) => {
        // Use the original persisted amount from the database to correctly calculate the base used amount
        // If not provided, fall back to the current request amount
        const persistedRequestAmount = originalRequestAmount ?? requestAmount;

        const { exceededStatuses, withinLimitStatuses } =
          spendLimitStatus.reduce(
            (
              accumulator: {
                exceededStatuses: SpendLimitPeriodStatus[];
                withinLimitStatuses: SpendLimitPeriodStatus[];
              },
              status,
            ) => {
              const { usedAmount, limitAmount, period } = status;
              const totalUsed =
                usedAmount + (period === 'request' ? requestAmount : 0);
              if (totalUsed > limitAmount) {
                accumulator.exceededStatuses.push(status);
              } else {
                accumulator.withinLimitStatuses.push(status);
              }
              return accumulator;
            },
            {
              exceededStatuses: [],
              withinLimitStatuses: [],
            },
          );

        const maxReimbursableAmount = calculateMaxReimbursableAmount(
          exceededStatuses,
          persistedRequestAmount,
        );

        return (
          <>
            {/* Show warning callout for exceeded limits (highest priority) */}
            {exceededStatuses.length > 0 && (
              <Callout
                className={className}
                variant="warning"
                title={t(
                  'spendLimits.modal.request.calloutWarning.limitExceeded',
                )}
              >
                <div className="flex flex-col content-stretch gap-16">
                  <ul className="list-inside list-disc space-y-2">
                    {exceededStatuses.map((status) =>
                      renderExceedLimitItem({ status, persistedRequestAmount }),
                    )}
                  </ul>
                  {setReimbursedAmount && exceededStatuses.length > 0 && (
                    <Button
                      variant="primaryWarning"
                      text={t('spendLimits.modal.request.calloutWarning.cta', {
                        amount: formatMoney(
                          maxReimbursableAmount,
                          exceededStatuses[0].currency,
                        ),
                      })}
                      onClick={() => setReimbursedAmount(maxReimbursableAmount)}
                    />
                  )}
                </div>
              </Callout>
            )}

            {/* Show info callout for within-limit statuses */}
            {withinLimitStatuses.length > 0 && (
              <Callout
                className={className}
                variant="info"
                title={t('spendLimits.modal.request.calloutInfo.title')}
              >
                <ul className="list-inside list-disc space-y-2">
                  {withinLimitStatuses.map(
                    ({ period, limitAmount, currency }) => {
                      const calloutTitleKey = getCalloutTitleKey(period);
                      return (
                        <li key={`within-limit-${period}`}>
                          {t(calloutTitleKey, {
                            amount: formatMoney(limitAmount, currency),
                          })}
                        </li>
                      );
                    },
                  )}
                </ul>
              </Callout>
            )}
          </>
        );
      }}
    </QuerySuspense>
  );
};

const CalloutLoading = () => {
  return <SkeletonText />;
};
